'use client';
import { Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { Button, useWindowDimensions } from '@get-mosh/msh-shared';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import { RichTextField, LinkField, KeyTextField } from '@prismicio/types';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons';

/**
 * Props for `BackgroundHeroBanner`.
 */
export type BackgroundHeroBannerProps =
    SliceComponentProps<Content.BackgroundHeroBannerSlice>;

/**
 * Component for "BackgroundHeroBanner" Slices.
 */
const BackgroundHeroBanner = ({
    slice,
}: BackgroundHeroBannerProps): JSX.Element => {
    const { width } = useWindowDimensions();
    const isMobile = width < 768;

    const title = isMobile
        ? slice.primary.mobile_title
        : slice.primary.desktop_title;
    const content = isMobile
        ? slice.primary.mobile_content
        : slice.primary.desktop_content;

    return (
        <section
            data-slice-type={slice.slice_type}
            data-slice-variation={slice.variation}
            className="relative flex h-full w-full"
        >
            <picture className="aspect-9/16 w-full md:aspect-video">
                <source
                    srcSet={slice.primary.large_image.url || undefined}
                    media="(min-width: 992px)"
                />
                <source
                    srcSet={slice.primary.medium_image.url || undefined}
                    media="(min-width: 768px) and (max-width: 991px)"
                />
                <source
                    srcSet={slice.primary.small_image.url || undefined}
                    media="(max-width: 767px)"
                />
                <PrismicNextImage field={slice.primary.large_image} fill />
            </picture>

            <div className="absolute inset-0 flex flex-col justify-center">
                <div className="container">
                    <HeroContent
                        title={title}
                        preHeading={slice.primary.pre_heading}
                        content={content}
                        ctaText={slice.primary.cta_text}
                        ctaLink={slice.primary.cta_link}
                        legalText={slice.primary.legal_text}
                        darkMode={slice.primary.dark_mode}
                        headingH2={slice.primary.heading_h2}
                    />
                </div>
            </div>
        </section>
    );
};

const HeroContent = ({
    title,
    preHeading,
    content,
    legalText,
    ctaText,
    ctaLink,
    darkMode = false,
    headingH2 = false,
}: {
    title: KeyTextField;
    preHeading: KeyTextField;
    content: RichTextField;
    legalText: RichTextField;
    ctaText: KeyTextField;
    ctaLink: LinkField;
    darkMode: boolean;
    headingH2: boolean;
}) => {
    return (
        <div className="flex max-w-[660px] flex-col items-start gap-6 md:gap-8">
            <div className="flex flex-col gap-4 md:gap-6">
                <div className="flex flex-col gap-2">
                    <h4
                        className={classNames({
                            'text-blue': !darkMode,
                            'text-white': darkMode,
                        })}
                    >
                        {preHeading}
                    </h4>
                    {headingH2 ? (
                        <h2
                            className={classNames({
                                'text-white': darkMode,
                            })}
                        >
                            {title}
                        </h2>
                    ) : (
                        <h1
                            className={classNames({
                                'text-white': darkMode,
                            })}
                        >
                            {title}
                        </h1>
                    )}
                </div>
                <PrismicRichText
                    field={content}
                    components={{
                        paragraph: ({ children }) => (
                            <h4
                                className={classNames('highlight', {
                                    'text-white': darkMode,
                                })}
                            >
                                {children}
                            </h4>
                        ),
                        listItem: ({ children }) => (
                            <li
                                className={classNames(
                                    'mb-2 flex items-start gap-3 pl-5',
                                    { 'text-white': darkMode },
                                )}
                            >
                                <FontAwesomeIcon
                                    icon={faCircleCheck}
                                    size="xl"
                                />
                                <h4 className="highlight">{children}</h4>
                            </li>
                        ),
                        hyperlink: ({ children, node }) => (
                            <a
                                className={classNames(
                                    'font-demi hover:underline',
                                    {
                                        'text-white': darkMode,
                                    },
                                )}
                                href={node.data.url}
                            >
                                {children}
                            </a>
                        ),
                    }}
                />
            </div>
            <div className="flex flex-col gap-2">
                {ctaLink && ctaText && 'url' in ctaLink ? (
                    <Button
                        as={PrismicNextLink}
                        field={ctaLink}
                        text={ctaText}
                        stretch
                    />
                ) : null}
                <div
                    className={classNames('flex flex-col pt-2', {
                        'text-white': darkMode,
                        'text-grey-400': !darkMode,
                    })}
                >
                    <PrismicRichText field={legalText} />
                </div>
            </div>
        </div>
    );
};

export default BackgroundHeroBanner;
